import { localStorageKeys } from '@sketch/constants';
import { useGetDiscoverHashQuery } from '@sketch/gql-types';
import { getParsedItem } from '@sketch/utils';
import { useState, useEffect, useRef, useCallback } from 'react';

const storageKey = localStorageKeys.discoverLastContentHash;
const getStorageItem = () => {
    return getParsedItem(storageKey);
};
const createTriggerStorageEvent = (oldValue) => (newValue) => {
    /**
     * We dispatch this fake event to allow the useReactiveAuthorization
     * to update it's internal values and. the local-storage event listener
     * only allows the receive changes from other tabs. This work-around will
     * allow it to work always.
     */
    const storageEvent = new StorageEvent('storage', {
        key: storageKey,
        storageArea: localStorage,
        newValue,
        oldValue,
    });
    window.dispatchEvent(storageEvent);
};
const useDiscover = () => {
    var _a;
    const [currentHash, setCurrentHash] = useState(getStorageItem());
    const { data } = useGetDiscoverHashQuery();
    /**
     * Given that this hook could be used more then one time
     * it's important that it stays sync with the latest local-storage
     * value, therefore we list to the events to make sure the state is synced
     */
    useEffect(() => {
        const handleStorageEvent = (event) => {
            event.key === storageKey && setCurrentHash(event.newValue);
        };
        window.addEventListener('storage', handleStorageEvent);
        return () => {
            window.removeEventListener('storage', handleStorageEvent);
        };
    }, []);
    const fileHash = (_a = data === null || data === void 0 ? void 0 : data.discover.contentHash) !== null && _a !== void 0 ? _a : '';
    const hasNewContent = fileHash !== currentHash;
    /**
     * Prevent the oldValue from triggering a new "setViewedDiscover" when changed
     * which can cause a re-render in the view
     */
    const triggerStorageEvent = useRef(createTriggerStorageEvent(currentHash));
    useEffect(() => {
        triggerStorageEvent.current = createTriggerStorageEvent(currentHash);
    }, [currentHash]);
    const setViewedDiscover = useCallback(() => {
        setCurrentHash(fileHash);
        triggerStorageEvent.current(fileHash);
    }, [fileHash]);
    return {
        hasNewContent,
        setViewedDiscover,
    };
};

export { useDiscover };
